import { CRITERION_VALUE } from "@/constants/criterion-values"
import { countPairedCompositeCriteriaAgreements } from "@/metrics/agreement"

import type { MetricMetadata } from "@/types/metrics/metadata"

import { getCompositeReportingMethod } from "./composite-evaluation-values"

function normalizeFloatForMethod(
  floatValue: number,
  reportingMethod: string,
  metricMetadata?: MetricMetadata,
): number | null {
  if (!Number.isFinite(floatValue)) {
    return null
  }
  if (reportingMethod === "all_criteria_met") {
    return floatValue >= 1 ? 100 : 0
  }
  if (reportingMethod === "count_of_criteria_met") {
    const criteria = metricMetadata?.criteria
    return Array.isArray(criteria) ? floatValue * criteria.length : null
  }
  return floatValue * 100
}

interface CriteriaAgreementCounts {
  matching: number
  total: number
}

export type CompositeAgreementMode = "criteria" | "score"

interface CompositeCriteriaAgreementSource {
  matched_criteria?: number
  comparable_criteria?: number
  excluded_human_criteria?: number
  missing_human_criteria?: number
  missing_machine_criteria?: number
  version_mismatched_criteria?: number
  criterion_groups?: Array<{
    criterion_identity: string
    criterion: string
    matched_criteria?: number
    comparable_criteria?: number
    excluded_human_criteria?: number
    missing_human_criteria?: number
    missing_machine_criteria?: number
    version_mismatched_criteria?: number
  }>
  total_criterion_groups?: number
  returned_criterion_groups?: number
  criterion_groups_truncated?: boolean
}

interface CompositeCriteriaAgreementSummary {
  matching: number
  total: number
  disagreements: number
  rate: number | null
  excludedHuman: number
  missingHuman: number
  missingMachine: number
  versionMismatched: number
  groups: Array<{
    identity: string
    criterion: string
    matching: number
    total: number
    rate: number | null
  }>
  groupsTruncated: boolean
}

export function summarizeCompositeCriteriaAgreement(
  source: CompositeCriteriaAgreementSource | null | undefined,
): CompositeCriteriaAgreementSummary | null {
  if (!source) {
    return null
  }
  const matching = source.matched_criteria ?? 0
  const total = source.comparable_criteria ?? 0
  return {
    matching,
    total,
    disagreements: Math.max(total - matching, 0),
    rate: total > 0 ? (matching / total) * 100 : null,
    excludedHuman: source.excluded_human_criteria ?? 0,
    missingHuman: source.missing_human_criteria ?? 0,
    missingMachine: source.missing_machine_criteria ?? 0,
    versionMismatched: source.version_mismatched_criteria ?? 0,
    groups: (source.criterion_groups ?? []).map((group) => {
      const groupMatching = group.matched_criteria ?? 0
      const groupTotal = group.comparable_criteria ?? 0
      return {
        identity: group.criterion_identity,
        criterion: group.criterion,
        matching: groupMatching,
        total: groupTotal,
        rate: groupTotal > 0 ? (groupMatching / groupTotal) * 100 : null,
      }
    }),
    groupsTruncated: source.criterion_groups_truncated ?? false,
  }
}

function extractIndividualResults(
  raw: Record<string, unknown> | null | undefined,
): string[] | null {
  const results = (raw as { individual_results?: unknown } | null | undefined)
    ?.individual_results
  if (!Array.isArray(results) || results.length === 0) {
    return null
  }
  return results.map((r) => {
    const item = r as { result?: unknown } | null | undefined
    return typeof item?.result === "string"
      ? item.result
      : CRITERION_VALUE.UNKNOWN
  })
}

/**
 * Extract per-criterion results from an AI composite metric output.
 * AI's stored `set_value` for composites can be an aggregate-shaped array
 * that doesn't line up with the criteria positions (or missing entirely),
 * so we prefer `result` / `result_json`'s `individual_results` — the same
 * source the drawer reads for its per-criterion badges — and only fall
 * back to `set_value` when no structured results exist.
 */
export function extractAiCriteriaResults(source: {
  set_value?: string[] | null
  result?: Record<string, unknown> | null
  result_json?: Record<string, unknown> | null
  json_value?: Record<string, unknown> | null
}): string[] | null {
  for (const raw of [source.result, source.result_json, source.json_value]) {
    const extracted = extractIndividualResults(raw)
    if (extracted) {
      return extracted
    }
  }
  if (source.set_value && source.set_value.length > 0) {
    return source.set_value
  }
  return null
}

/**
 * Extract per-criterion results from a human review. `set_value` is the
 * authoritative source (one entry per criterion, empty string for unlabeled).
 * `json_value.individual_results` is used only for criterion identity elsewhere;
 * its result can contain the AI fallback for criteria the human did not click.
 */
export function extractHumanCriteriaResults(source: {
  set_value?: string[] | null
  json_value?: Record<string, unknown> | null
}): string[] | null {
  return source.set_value && source.set_value.length > 0
    ? source.set_value
    : null
}

/**
 * Compare AI criteria to human criteria position-by-position and return
 * {matching, total}. Only counts criteria where the human gave a definite
 * answer (MET or NOT_MET); UNKNOWN/empty entries are excluded from both
 * the numerator and the denominator. Callers accumulate these across sims
 * to compute per-criterion agreement rates.
 */
export function countCriteriaAgreements(
  aiCriteria: readonly string[] | null | undefined,
  humanCriteria: readonly string[] | null | undefined,
): CriteriaAgreementCounts {
  if (!aiCriteria || !humanCriteria) {
    return { matching: 0, total: 0 }
  }
  let matching = 0
  let total = 0
  for (let i = 0; i < humanCriteria.length; i++) {
    const h = humanCriteria[i]
    if (!h || h === CRITERION_VALUE.UNKNOWN) {
      continue
    }
    total++
    if (aiCriteria[i] === h) {
      matching++
    }
  }
  return { matching, total }
}

type CompositeAgreementOutcome =
  /** No comparable data on at least one side; caller should skip. */
  | { kind: "skip" }
  /**
   * Per-criterion comparison ran (both sides had criteria data).
   * `matching` and `total` are per-criterion counts; total===0 means both
   * had criterion arrays but none of the human's entries were definite.
   */
  | { kind: "per-criterion"; matching: number; total: number }
  /** Explicit aggregate-score comparison; `agrees` is one row-level boolean. */
  | { kind: "aggregate"; agrees: boolean }

/**
 * Single source of truth for "does this AI/human pair agree on a composite
 * metric row."
 *
 * Criteria mode pairs normalized criterion text plus duplicate occurrence and
 * never falls back to a scalar score. Missing structured details are
 * non-comparable. Score mode compares only normalized aggregate floats.
 */
export function compareCompositeAgreement(
  aiSource: {
    float_value?: number | null
    set_value?: string[] | null
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  },
  humanSource: {
    float_value?: number | null
    set_value?: string[] | null
    json_value?: Record<string, unknown> | null
  },
  metricMetadata?: MetricMetadata,
  mode: CompositeAgreementMode = "criteria",
): CompositeAgreementOutcome {
  if (mode === "criteria") {
    const agreement = countPairedCompositeCriteriaAgreements(
      aiSource,
      humanSource,
    )
    if (!agreement) {
      return { kind: "skip" }
    }
    const { matching, total } = agreement
    return { kind: "per-criterion", matching, total }
  }

  const resultReportingMethod = aiSource.result?.reporting_method
  const legacyReportingMethod = aiSource.result_json?.reporting_method
  const executionReportingMethod =
    typeof resultReportingMethod === "string"
      ? resultReportingMethod
      : typeof legacyReportingMethod === "string"
        ? legacyReportingMethod
        : undefined
  const reportingMethod =
    executionReportingMethod ?? getCompositeReportingMethod(metricMetadata)
  const aiNormalized =
    aiSource.float_value == null
      ? null
      : normalizeFloatForMethod(
          aiSource.float_value,
          reportingMethod,
          metricMetadata,
        )
  const humanNormalized =
    humanSource.float_value == null
      ? null
      : normalizeFloatForMethod(
          humanSource.float_value,
          reportingMethod,
          metricMetadata,
        )
  if (aiNormalized == null || humanNormalized == null) {
    return { kind: "skip" }
  }
  return { kind: "aggregate", agrees: aiNormalized === humanNormalized }
}

/**
 * Return the agreement percentage for one human/machine Composite pair.
 * Criteria mode uses matched definite criterion pairs; score mode is a
 * single aggregate comparison expressed as 100% or 0%.
 */
export function getCompositeAgreementRate(
  aiSource: Parameters<typeof compareCompositeAgreement>[0],
  humanSource: Parameters<typeof compareCompositeAgreement>[1],
  metricMetadata?: MetricMetadata,
  mode: CompositeAgreementMode = "criteria",
): number | null {
  const outcome = compareCompositeAgreement(
    aiSource,
    humanSource,
    metricMetadata,
    mode,
  )
  if (outcome.kind === "skip") {
    return null
  }
  if (outcome.kind === "aggregate") {
    return outcome.agrees ? 100 : 0
  }
  return outcome.total > 0 ? (outcome.matching / outcome.total) * 100 : null
}
